
import { useState } from 'react';
import Header from '../../components/feature/Header';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    message: ''
  });
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.company || !formData.name || !formData.email) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    if (!agreePrivacy) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d3vh2jqb9hjta443jgrg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          company: formData.company,
          name: formData.name,
          email: formData.email,
          message: formData.message,
          privacy_agreement: agreePrivacy ? 'true' : 'false'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', name: '', email: '', message: '' });
        setAgreePrivacy(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            문의하기
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            올딩스 로지스에게 빠르고 간편하게 문의 하세요!
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6">
          <form 
            onSubmit={handleSubmit} 
            className="bg-white shadow-lg rounded-lg p-8 border"
            data-readdy-form
            id="contact_inquiry"
          >
            <div className="space-y-6">
              {/* Company Name */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  업체명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="회사명을 입력해주세요"
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="담당자명을 입력해주세요"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="이메일 주소를 입력해주세요"
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  문의내용 <span className="text-gray-500">(선택사항)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="문의하실 내용을 자세히 입력해주세요 (최대 500자)"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              {/* Privacy Agreement */}
              <div className="border-t pt-6">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={agreePrivacy}
                    onChange={(e) => setAgreePrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <label htmlFor="privacy" className="text-sm text-gray-700">
                      개인정보 수집 및 이용 동의 <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="ml-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      전문보기
                    </button>
                  </div>
                </div>
              </div>

              {/* Submit Status */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <i className="ri-check-circle-fill text-green-400 text-xl"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        문의가 성공적으로 접수되었습니다!
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        빠른 시일 내에 연락드리겠습니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <i className="ri-error-warning-fill text-red-400 text-xl"></i>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">
                        문의 접수 중 오류가 발생했습니다.
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        잠시 후 다시 시도해주세요.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || !agreePrivacy}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors whitespace-nowrap"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    문의 접수 중...
                  </div>
                ) : (
                  '문의하기'
                )}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">연락처 정보</h2>
            <p className="text-gray-600">다른 방법으로도 연락하실 수 있습니다</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-fill text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">전화 문의</h3>
              <p className="text-gray-600">010-3099-5677</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-fill text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">이메일 문의</h3>
              <p className="text-gray-600">ydhan66@gmail.com</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-fill text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">운영 시간</h3>
              <p className="text-gray-600">평일 09:00 - 18:00</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">개인정보 수집 및 이용 동의</h3>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                <p className="mb-4">
                  <strong>'올딩스 로지스'</strong>는 (이하 '올딩스 로지스'는) 고객님의 개인정보를 중요시하며, "정보통신망 이용촉진 및 정보보호"에 관한 법률을 준수하고 있습니다.
                </p>
                
                <p className="mb-4">
                  회사는 개인정보취급방침을 통하여 고객님께서 제공하시는 개인정보가 어떠한 용도와 방식으로 이용되고 있으며, 개인정보보호를 위해 어떠한 조치가 취해지고 있는지 알려드립니다.
                </p>
                
                <p className="mb-4">
                  로지프렌드는 개인정보취급방침을 개정하는 경우 웹사이트 공지사항(또는 개별공지)을 통하여 공지할 것입니다.
                </p>
                
                <p className="mb-6">
                  <strong>본 방침은 : 2025년 11월 01일 부터 시행됩니다.</strong>
                </p>
                
                <h4 className="font-semibold text-gray-900 mb-3">■ 수집하는 개인정보 항목</h4>
                <p className="mb-4">
                  로지프렌드는 상담, 견적요청 등등을 위해 아래와 같은 개인정보를 수집하고 있습니다.
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>수집항목 : 이름, 업체명, 연락처, 이메일</li>
                  <li>개인정보 수집방법 : 홈페이지(문의하기)를 통한 문의</li>
                </ul>
                
                <h4 className="font-semibold text-gray-900 mb-3">■ 개인정보의 수집 및 이용목적</h4>
                <p className="mb-4">
                  로지프렌드는 수집한 개인정보를 다음의 목적을 위해 활용합니다.
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>문의내용 확인 및 안내를 위한 연락</li>
                </ul>
                
                <h4 className="font-semibold text-gray-900 mb-3">■ 개인정보의 수집 및 보유기간</h4>
                <p className="mb-4">
                  고객의 개인정보는 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기하며, 보유기간은 최대 3년을 넘기지 않는 것을 원칙으로 합니다.
                </p>
                
                <h4 className="font-semibold text-gray-900 mb-3">■ 개인정보의 자동 수집 장치의 설치/운영 및 거부에 관한 사항</h4>
                <p className="mb-4">
                  당 홈페이지는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 이용정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용하지 않습니다.
                </p>
                
                <h4 className="font-semibold text-gray-900 mb-3">■ 개인정보에 관한 민원서비스</h4>
                <p className="mb-4">
                  로지프렌드는 고객의 개인정보를 보호하고 개인정보와 관련한 불만을 처리하기 위하여 아래와 같이 개인정보관리책임자를 지정하고 있습니다.
                </p>
                <ul className="list-disc pl-6 mb-4">
                  <li>개인정보관리책임자 : 한영대</li>
                  <li>전화번호 : 010-3099-5677</li>
                  <li>이메일 : ydhan66@gmail.com</li>
                </ul>
                
                <p className="mb-4">
                  기타 개인정보침해에 대한 신고나 상담이 필요하신 경우에는 아래 기관에 문의하시기 바랍니다.
                </p>
                <ol className="list-decimal pl-6 mb-4">
                  <li>개인정보침해신고센터 (www.1336.or.kr/국번없이 118)</li>
                  <li>정보보호마크인증위원회 (www.eprivacy.or.kr/02-580-0533~4)</li>
                  <li>대검찰청 인터넷범죄수사센터 (http://icic.sppo.go.kr/02-3480-3600)</li>
                  <li>경찰청 사이버테러대응센터 (www.ctrc.go.kr/02-392-0330)</li>
                </ol>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg whitespace-nowrap"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
